/**
 * FloatingNodeLabel — Floating name label positioned above the node card.
 *
 * Renders the node name as a semi-transparent label above the top-left corner
 * of the card. Uses nopan/nodrag classes so it doesn't interfere with
 * ReactFlow dragging or panning.
 *
 * Sprint 10f: Redesign node name area
 * Sprint 3 (Task 9): Added inline editing — hover shows pencil icon,
 * click enters edit mode with auto-focused input, blur saves, Escape cancels.
 */

import { memo, useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Pencil } from 'lucide-react'
import { NODE_BG_COLORS } from './nodeBgColors'
import { createNadDebug } from '../../../../utils/debugLog'
import { useNodePresentation } from '../../NodePresentationContext'

// Feature tag `dw:asset-grid-label-bleed` corresponds to RFC asset-grid label
// bleed-through fix. The label sits in the 26-px CHILD_LABEL_HEIGHT band above
// each card; if its background is transparent, content from neighbouring
// designer cards (subtitle pills, Quick Info Bar, Quality "Passed" chip) bleeds
// through.
const labelDebug = createNadDebug('dw:asset-grid-label-bleed')

// Match the default card body (`nodeBgColors.ts:2`) so the label visually
// merges with the card chrome instead of floating as a contrasting pill.
const DEFAULT_LABEL_BG = NODE_BG_COLORS.default.value

export interface FloatingNodeLabelProps {
  /** The name to display */
  name: string
  /** Fallback text when name is empty */
  fallback?: string
  /** Optional SVG icon to display before the name */
  typeIcon?: React.ReactNode
  /** Callback when the user renames the node. If omitted, editing is disabled. */
  onRename?: (newName: string) => void
  /** Optional background color for the label pill (e.g. from node color selection) */
  bgColor?: string
}

/**
 * Hold the name at one size on screen, whatever the zoom.
 *
 * The whole canvas is already scaled by `zoom`, so scaling the label by
 * `1 / zoom` cancels that out exactly: 14px of text renders as 14px at 10% and
 * at 400% alike. The label is chrome for finding your way around, not part of
 * the artwork, so it should not shrink into nothing when you pull back or
 * balloon when you push in.
 *
 * This applies to EVERY label, on the full card and on the simple low-zoom card
 * alike, and that is the point. Applying it to only one of them made the name
 * jump size at the moment the canvas crossed the lightweight-render threshold.
 * One rule for both means the only thing that changes there is the card body.
 *
 * Done in CSS off `--canvas-viewport-zoom`, a variable `WorkspacePageV2`
 * already writes to the ReactFlow root on every viewport move, so it costs no
 * React render. Reading the zoom through `useViewportZoom` instead would
 * re-render every node on every frame of a zoom — the exact work the low-zoom
 * card exists to avoid.
 *
 * Anchored at `bottom: 100%`, so scaling from the bottom-left keeps the label
 * pinned just above the card's top-left corner and lets it grow up and right.
 */
const CONSTANT_SCREEN_SIZE_STYLE: React.CSSProperties = {
  transform: 'scale(calc(1 / var(--canvas-viewport-zoom, 1)))',
  transformOrigin: 'bottom left',
}

const FloatingNodeLabel = memo(function FloatingNodeLabel({
  name,
  fallback = 'Untitled',
  typeIcon,
  onRename,
  bgColor,
}: FloatingNodeLabelProps) {
  const { t } = useTranslation('canvas')
  const inline = useNodePresentation() === 'detail'
  const placementStyle: React.CSSProperties = inline
    ? { position: 'relative', marginBottom: 8 }
    : { bottom: '100%', marginBottom: 6, ...CONSTANT_SCREEN_SIZE_STYLE }
  const displayName = name || fallback

  const [isEditing, setIsEditing] = useState(false)
  const [editValue, setEditValue] = useState(name)
  const inputRef = useRef<HTMLInputElement>(null)

  // When entering edit mode, sync editValue to current name and focus/select
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [isEditing])

  const handleStartEditing = useCallback(() => {
    if (!onRename) return
    setEditValue(name)
    setIsEditing(true)
  }, [onRename, name])

  const handleSave = useCallback(() => {
    setIsEditing(false)
    const trimmed = editValue.trim()
    if (trimmed && trimmed !== name && onRename) {
      onRename(trimmed)
    }
  }, [editValue, name, onRename])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        setEditValue(name)
        setIsEditing(false)
      } else if (e.key === 'Enter') {
        e.preventDefault()
        handleSave()
      }
    },
    [name, handleSave],
  )

  // Always paint an opaque background so nothing from cards beneath bleeds
  // through the floating label band. When the user picked a custom node
  // colour, honour it; otherwise fall back to the default card body colour.
  const effectiveBgColor = bgColor || DEFAULT_LABEL_BG
  const hasCustomBgColor = Boolean(bgColor)

  useEffect(() => {
    labelDebug({
      event: 'label-mounted',
      mode: hasCustomBgColor ? 'user-bgColor' : 'token-default',
      bgColor: effectiveBgColor,
      hasName: Boolean(name),
    })
  }, [effectiveBgColor, hasCustomBgColor, name])

  if (isEditing) {
    return (
      <div
        data-testid="floating-node-label"
        className="absolute left-1.5 nopan nodrag nowheel"
        style={{
          ...placementStyle,
        }}
      >
        <input
          ref={inputRef}
          data-testid="floating-node-label-input"
          type="text"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={handleSave}
          onKeyDown={handleKeyDown}
          className="text-sm font-semibold text-foreground/80 bg-transparent border-b border-foreground/30 focus:border-primary outline-none max-w-[300px] px-0 py-0"
          style={{ minWidth: 60 }}
        />
      </div>
    )
  }

  return (
    <div
      data-testid="floating-node-label"
      className={`absolute left-1.5 nopan nodrag select-none group/label ${onRename ? '' : 'pointer-events-none'}`}
      style={{
        ...placementStyle,
      }}
    >
      <div
        data-testid="floating-node-label-pill"
        data-label-bg-mode={hasCustomBgColor ? 'user-bgColor' : 'token-default'}
        className="flex items-center gap-1 max-w-[340px]"
        style={{
          backgroundColor: effectiveBgColor,
          borderRadius: 6,
          paddingLeft: 8,
          paddingRight: 8,
          paddingTop: 2,
          paddingBottom: 2,
        }}
      >
        {typeIcon && (
          <span className="w-3.5 h-3.5 flex-shrink-0 text-foreground/50">
            {typeIcon}
          </span>
        )}
        <span
          className="text-sm font-semibold truncate"
          style={{ color: hasCustomBgColor ? '#ffffff' : undefined, opacity: hasCustomBgColor ? 1 : 0.8 }}
        >
          {displayName}
        </span>
        {onRename && (
          <button
            data-testid="floating-node-label-edit-btn"
            onClick={(e) => {
              e.stopPropagation()
              handleStartEditing()
            }}
            className="opacity-0 group-hover/label:opacity-100 transition-opacity w-5 h-5 rounded hover:bg-surface-1 text-foreground/50 hover:text-foreground flex items-center justify-center flex-shrink-0"
            title={t('floatingNodeLabel.rename')}
          >
            <Pencil size={12} />
          </button>
        )}
      </div>
    </div>
  )
})

FloatingNodeLabel.displayName = 'FloatingNodeLabel'
export { FloatingNodeLabel }
